import React, { FunctionComponent, useState } from 'react'
import useTranslation from '../../hooks/useTranslation'
import LastConfigurationInfo from '../../entities/LastConfigurationInfo'
import useOnMount from '../../hooks/useOnMount'
import LoadingIndicatorWrapper from '../loading-indicator-wrapper'
import { Spinner } from '../../base-components'
import style from './lastConfigurations.css'
import LastConfigurationRow from './LastConfigurationRow'
import LastConfigurationsDateFilter from '../last-configurations-date-filter'
import { Table } from '../table'
import LastConfigurationsTablePlaceholder from './LastConfigurationsTablePlaceholder'
import { ROUTE_LAST_CONFIGURATIONS } from '../../constants/routes'

export type Props = {
    lastConfigurationsInfo: LastConfigurationInfo
    isLoading: boolean
    loadLastConfigurationsInfo: () => {}
    loadLastConfigurationsForDate: (date: string) => void
    loadConfigurationByVehicleCode: (vehicleCode: string) => {}
}

const LastConfigurations: FunctionComponent<Props> = (props) => {
    const {
        lastConfigurationsInfo,
        isLoading,
        loadLastConfigurationsInfo,
        loadLastConfigurationsForDate,
        loadConfigurationByVehicleCode,
    } = props
    const { lastConfigurations, lastConfigurationsAvailabilities, lastConfigurationsLoadingDate } = lastConfigurationsInfo
    const { t } = useTranslation()

    const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined)

    useOnMount(() => {
        loadLastConfigurationsInfo && loadLastConfigurationsInfo()
    })

    const hasCachedDataForSelectedDate =
        selectedDate != null && lastConfigurations.some((lc) => lc.date === selectedDate)
    const isLoadingForSelectedDate =
        selectedDate != null && lastConfigurationsLoadingDate === selectedDate
    const isInitialLoading = isLoadingForSelectedDate && !hasCachedDataForSelectedDate
    const isRevalidating = isLoadingForSelectedDate && hasCachedDataForSelectedDate

    const isWaitingForAvailabilities = isLoading && lastConfigurationsAvailabilities.length === 0
    const isWaitingForConfigurations = isLoading && isInitialLoading
    const showPlaceholder = isWaitingForAvailabilities || isWaitingForConfigurations

    const configurationItems = (lastConfigurations.find((lc) => lc.date === selectedDate)?.configurations ?? [])
        .map((lastConfig) => (
            <LastConfigurationRow
                key={lastConfig.vehicleCode}
                configurationInfo={lastConfig}
                loadConfigurationByVehicleCode={loadConfigurationByVehicleCode}
            />
        ))

    const configurationsTable = showPlaceholder
        ? <LastConfigurationsTablePlaceholder/>
        : (
            <Table className={style.content}>
                <tbody>
                    {configurationItems}
                </tbody>
            </Table>
        )

    return (
        <div className={style.container} data-testid={`page-${ROUTE_LAST_CONFIGURATIONS}`}>
            <section className={style.headerContainer}>
                <h1 className={style.title}>
                    {t('garage.lastConfigurations.heading')}
                </h1>
            </section>
            <div className={style.loadingIndicator}>
                {isRevalidating && (
                    <>
                        <Spinner height="16px" width="16px" />
                        <span className={style.loadingText}>
                            {t('garage.lastConfigurations.updatingConfigurationsText')}
                        </span>
                    </>
                )}
            </div>
            <LoadingIndicatorWrapper isLoading={showPlaceholder}>
                <LastConfigurationsDateFilter
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    onDateChange={loadLastConfigurationsForDate}
                />
                {configurationsTable}
            </LoadingIndicatorWrapper>
        </div>
    )
}

export default LastConfigurations
